"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { Refresh as Loader2 } from "@papier/ui/components/icons"
import { useContext, useEffect, useRef } from "react"
import { useForm } from "react-hook-form"
import * as z from "zod"

import { AuthUIContext } from "../../../lib/auth-ui-provider"
import { getLocalizedError, getPasswordSchema } from "../../../lib/utils"
import { cn } from "@papier/ui/lib/utils"
import type { AuthLocalization } from "../../../localization/auth-localization"
import type { PasswordValidation } from "../../../types/password-validation"
import { PasswordInput } from "../../password-input"
import { PasswordStrengthMeter } from "../../password-strength-meter"
import { Button } from "@papier/ui/components/ui/button"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from "@papier/ui/components/ui/form"
import type { AuthFormClassNames } from "../auth-form"

export interface ResetPasswordFormProps {
    className?: string
    classNames?: AuthFormClassNames
    localization: Partial<AuthLocalization>
    passwordValidation?: PasswordValidation
}

function getSafeSignInSearch(search: string): string {
    const redirectTo = new URLSearchParams(search).get("redirectTo")
    if (!redirectTo) return ""

    const nextSearch = new URLSearchParams({ redirectTo })
    return `?${nextSearch.toString()}`
}

function getExternalOnboarding(search: string): null | {
    email: string
    redirectTo: string
} {
    const searchParams = new URLSearchParams(search)
    if (searchParams.get("externalOnboarding") !== "1") return null

    const email = searchParams.get("externalEmail")?.trim()
    const redirectTo = searchParams.get("redirectTo")
    if (!email || !redirectTo || !redirectTo.startsWith("/")) return null
    if (redirectTo.startsWith("//") || redirectTo.includes("\\")) return null

    try {
        const redirectUrl = new URL(redirectTo, window.location.origin)
        if (
            redirectUrl.origin !== window.location.origin ||
            (!redirectUrl.pathname.startsWith("/shared-files/") &&
                redirectUrl.pathname !== "/external-files")
        ) {
            return null
        }

        return {
            email,
            redirectTo: `${redirectUrl.pathname}${redirectUrl.search}${redirectUrl.hash}`
        }
    } catch {
        return null
    }
}

export function ResetPasswordForm({
    className,
    classNames,
    localization,
    passwordValidation
}: ResetPasswordFormProps) {
    const tokenChecked = useRef(false)

    const {
        authClient,
        basePath,
        credentials,
        localization: contextLocalization,
        viewPaths,
        replace,
        toast
    } = useContext(AuthUIContext)

    const confirmPasswordEnabled = credentials?.confirmPassword
    const contextPasswordValidation = credentials?.passwordValidation

    localization = { ...contextLocalization, ...localization }
    passwordValidation = { ...contextPasswordValidation, ...passwordValidation }

    const formSchema = z
        .object({
            newPassword: getPasswordSchema(passwordValidation, {
                PASSWORD_REQUIRED: localization.NEW_PASSWORD_REQUIRED,
                PASSWORD_TOO_SHORT: localization.PASSWORD_TOO_SHORT,
                PASSWORD_TOO_LONG: localization.PASSWORD_TOO_LONG,
                INVALID_PASSWORD: localization.INVALID_PASSWORD
            }),
            confirmPassword: confirmPasswordEnabled
                ? getPasswordSchema(passwordValidation, {
                      PASSWORD_REQUIRED: localization.CONFIRM_PASSWORD_REQUIRED,
                      PASSWORD_TOO_SHORT: localization.PASSWORD_TOO_SHORT,
                      PASSWORD_TOO_LONG: localization.PASSWORD_TOO_LONG,
                      INVALID_PASSWORD: localization.INVALID_PASSWORD
                  })
                : z.string().optional()
        })
        .refine(
            (data) =>
                !confirmPasswordEnabled ||
                data.newPassword === data.confirmPassword,
            {
                message: localization.PASSWORDS_DO_NOT_MATCH,
                path: ["confirmPassword"]
            }
        )

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            newPassword: "",
            confirmPassword: ""
        }
    })

    const isSubmitting = form.formState.isSubmitting

    useEffect(() => {
        if (tokenChecked.current) return
        tokenChecked.current = true

        const searchParams = new URLSearchParams(window.location.search)
        const token = searchParams.get("token")

        if (!token || token === "INVALID_TOKEN") {
            replace(
                `${basePath}/${viewPaths.SIGN_IN}${getSafeSignInSearch(window.location.search)}`
            )
            toast({ variant: "error", message: localization.INVALID_TOKEN })
        }
    }, [basePath, replace, toast, viewPaths, localization])

    async function resetPassword({ newPassword }: z.infer<typeof formSchema>) {
        const externalOnboarding = getExternalOnboarding(
            window.location.search
        )

        try {
            const searchParams = new URLSearchParams(window.location.search)
            const token = searchParams.get("token") as string

            await authClient.resetPassword({
                newPassword,
                token,
                fetchOptions: { throw: true }
            })

            toast({
                variant: "success",
                message: localization.RESET_PASSWORD_SUCCESS
            })
        } catch (error) {
            toast({
                variant: "error",
                message: getLocalizedError({ error, localization })
            })

            form.reset()
            return
        }

        if (externalOnboarding) {
            try {
                const response = await authClient.signIn.email({
                    email: externalOnboarding.email,
                    password: newPassword,
                    rememberMe: true,
                    fetchOptions: { throw: true }
                })
                if (response.twoFactorRedirect) {
                    const search = new URLSearchParams({
                        redirectTo: externalOnboarding.redirectTo
                    })
                    replace(
                        `${basePath}/${viewPaths.TWO_FACTOR}?${search.toString()}`
                    )
                    return
                }
                replace(externalOnboarding.redirectTo)
                return
            } catch {
                toast({
                    variant: "error",
                    message:
                        "Parola a fost setată, dar autentificarea automată a eșuat. Autentifică-te folosind noua parolă."
                })
            }
        }

        replace(
            `${basePath}/${viewPaths.SIGN_IN}${getSafeSignInSearch(window.location.search)}`
        )
    }

    return (
        <Form {...form}>
            <form
                onSubmit={(event) => {
                    void form.handleSubmit(resetPassword)(event)
                }}
                className={cn("grid w-full gap-6", className, classNames?.base)}
            >
                <FormField
                    control={form.control}
                    name="newPassword"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className={classNames?.label}>
                                {localization.NEW_PASSWORD}
                            </FormLabel>

                            <FormControl>
                                <PasswordInput
                                    autoComplete="new-password"
                                    className={classNames?.input}
                                    placeholder={
                                        localization.NEW_PASSWORD_PLACEHOLDER
                                    }
                                    disabled={isSubmitting}
                                    {...field}
                                />
                            </FormControl>

                            <PasswordStrengthMeter
                                password={field.value}
                            />

                            <FormMessage className={classNames?.error} />
                        </FormItem>
                    )}
                />

                {confirmPasswordEnabled && (
                    <FormField
                        control={form.control}
                        name="confirmPassword"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className={classNames?.label}>
                                    {localization.CONFIRM_PASSWORD}
                                </FormLabel>

                                <FormControl>
                                    <PasswordInput
                                        autoComplete="new-password"
                                        className={classNames?.input}
                                        placeholder={
                                            localization.CONFIRM_PASSWORD_PLACEHOLDER
                                        }
                                        disabled={isSubmitting}
                                        {...field}
                                    />
                                </FormControl>

                                <FormMessage className={classNames?.error} />
                            </FormItem>
                        )}
                    />
                )}

                <Button
                    type="submit"
                    disabled={isSubmitting}
                    className={cn(
                        "w-full",
                        classNames?.button,
                        classNames?.primaryButton
                    )}
                >
                    {isSubmitting ? (
                        <Loader2 className="animate-spin" />
                    ) : (
                        localization.RESET_PASSWORD_ACTION
                    )}
                </Button>
            </form>
        </Form>
    )
}
